import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Calendar, Download, MessageSquare, Phone, File, Construction } from "lucide-react";
import { CommunicationFilters } from './communications/CommunicationFilters';
import { CommunicationTabs } from './communications/CommunicationTabs';
import { CommunicationList } from './communications/CommunicationList';
import { TimeRange, CommunicationStatus, CommunicationType, Communication } from './communications/types';

const CommunicationsMonitor = () => {
  const [selectedTab, setSelectedTab] = useState<CommunicationType>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [selectedStatus, setSelectedStatus] = useState<CommunicationStatus>("all");

  // Communications state (to be populated via integrations)
  const [communications, setCommunications] = useState<Communication[]>([]);
  // TODO: replace placeholder filter logic when data is available
  const filteredCommunications = communications;

  const downloadArchive = () => {
    // In a real application, this would trigger a download of the selected communications
    alert("In a production environment, this would download the selected communications archive");
  };

  return (
    <div className="relative">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-white/95 backdrop-blur-sm border border-orange-200 rounded-lg z-10 flex items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="flex justify-center">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full">
              <Construction className="h-12 w-12 text-orange-600" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">Communications Monitor</h3>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-orange-700">Coming Soon</span>
            </div>
          </div>
          <p className="text-gray-600 max-w-md mx-auto text-sm leading-relaxed">
            We're developing advanced AI-powered communication monitoring capabilities including 
            email surveillance, chat analysis, and automated compliance flagging.
          </p>
          <div className="pt-2">
            <div className="text-xs text-gray-500">Expected Release: Q3 2025</div>
          </div>
        </div>
      </div>

      {/* Original Component Content (kept for structure) */}
      <Card className="w-full bg-white shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              Communications Monitor
            </CardTitle>
            <Button onClick={downloadArchive} variant="outline" size="sm" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Archive
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and filters */}
          <CommunicationFilters 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
          />

          {/* Communication type tabs */}
          <CommunicationTabs 
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />

          {/* Communications list or empty states */}
          {selectedTab === 'call' ? (
            <div className="border rounded-lg h-[200px] flex flex-col items-center justify-center space-y-4">
              <Phone className="h-12 w-12 text-gray-300" />
              <h4 className="text-lg font-semibold">Calls Coming Soon</h4>
              <p className="text-sm text-gray-500 text-center">
                We're working on integrating call logs. Stay tuned!
              </p>
            </div>
          ) : filteredCommunications.length > 0 ? (
            <>
              <CommunicationList communications={filteredCommunications} />
              <div className="mt-4 flex justify-between items-center text-sm text-gray-500">
                <span>{filteredCommunications.length} communications displayed</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Last sync: {new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </>
          ) : selectedTab === 'chat' ? (
            <div className="border rounded-lg h-[200px] flex flex-col items-center justify-center space-y-4">
              <MessageSquare className="h-12 w-12 text-gray-300" />
              <h4 className="text-lg font-semibold">Connect your Chat</h4>
              <p className="text-sm text-gray-500 text-center">
                Integrate your chat platform (Slack, Teams, Discord) to monitor communications.
              </p>
              <Button onClick={() => alert('Chat integration flow coming soon')}>
                Connect Chat
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg h-[200px] flex flex-col items-center justify-center space-y-4">
              <Mail className="h-12 w-12 text-gray-300" />
              <h4 className="text-lg font-semibold">Connect your Email</h4>
              <p className="text-sm text-gray-500 text-center">
                Connect your corporate email account (Outlook, Gmail, etc.) to monitor inbound and outbound messages.
              </p>
              <Button onClick={() => alert('Email authentication flow coming soon')}>
                Connect Email
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationsMonitor;
